// ============================================================
//  SHARED LANDING THEME HELPER
//  ------------------------------------------------------------
//  Single source of truth for the brand-theme customizer:
//   - color math (hex/hsl, luminance, contrast, ramp derivation)
//   - the authored preset starting points
//   - reading/writing theme state to URL query params
//   - applying a theme state to a DOM element (the real CSS vars
//     defined in resources/assets/sass/apps/event/shared/_theme.scss)
//
//  Imported by BOTH bundles:
//   - event-module (the settings modal: ramp/contrast + query build)
//   - event-landing-ui (public pages: apply theme from query on init)
//
//  Pure functions only — no Vue / framework dependency.
// ============================================================

/* ------------------------------------------------------------
   PRESET STARTING POINTS (the authored landing themes)
   Mirrors theme-customization-example.html.
   ------------------------------------------------------------ */
export const THEME_PRESETS = {
  default:   { primary: '#4A6741', secondary: '#C87359', bg: '#faf9f7', surface: '#ffffff', headingFont: '"Lora", Georgia, serif',                       bodyFont: '"Nunito", -apple-system, sans-serif' },
  warm:      { primary: '#C87359', secondary: '#6B8A6E', bg: '#faf6ef', surface: '#ffffff', headingFont: '"Lora", Georgia, serif',                       bodyFont: '"Nunito", -apple-system, sans-serif' },
  botanical: { primary: '#B56C4B', secondary: '#7A8C5E', bg: '#faf6ef', surface: '#ffffff', headingFont: '"Lora", Georgia, serif',                       bodyFont: '"Nunito", -apple-system, sans-serif' },
  sunbaked:  { primary: '#C08428', secondary: '#7D7A3A', bg: '#faf6ef', surface: '#ffffff', headingFont: '"Lora", Georgia, serif',                       bodyFont: '"Nunito", -apple-system, sans-serif' },
  coastal:   { primary: '#557A90', secondary: '#8A8268', bg: '#faf6ef', surface: '#ffffff', headingFont: '"Lora", Georgia, serif',                       bodyFont: '"Nunito", -apple-system, sans-serif' },
  saas:      { primary: '#4F46E5', secondary: '#10B981', bg: '#f8fafc', surface: '#ffffff', headingFont: '"Inter", system-ui, sans-serif',              bodyFont: '"Inter", -apple-system, sans-serif' },
  impact:    { primary: '#E1462F', secondary: '#15803D', bg: '#fafafa', surface: '#ffffff', headingFont: '"Fraunces", Georgia, serif',                   bodyFont: '"Inter", -apple-system, sans-serif' },
  material:  { primary: '#1976D2', secondary: '#43A047', bg: '#f5f5f5', surface: '#ffffff', headingFont: '"Roboto", "Helvetica Neue", Arial, sans-serif', bodyFont: '"Roboto", -apple-system, sans-serif' },
};

// Query-param keys recognised on both sides. `thema` is the preset key;
// the rest are the customizable token values.
export const THEME_PARAM_KEYS = ['thema', 'primary', 'secondary', 'bg', 'surface', 'headingFont', 'bodyFont'];

const COLOR_KEYS = ['primary', 'secondary', 'bg', 'surface'];

/* ============================================================
   COLOR MATH — hex <-> hsl, luminance, contrast
   ============================================================ */
export function hexToRgb(hex) {
  hex = String(hex).replace('#', '');
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  const n = parseInt(hex, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex(r, g, b) {
  const h = (x) => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, '0');
  return ('#' + h(r) + h(g) + h(b)).toUpperCase();
}

export function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h;
  let s;
  const l = (max + min) / 2;
  if (max === min) {
    h = 0;
    s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

export function hslToHex(h, s, l) {
  h /= 360; s /= 100; l /= 100;
  let r;
  let g;
  let b;
  if (s === 0) {
    r = l; g = l; b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return rgbToHex(r * 255, g * 255, b * 255);
}

export function rgba(hex, a) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function relLum(hex) {
  const { r, g, b } = hexToRgb(hex);
  const f = (c) => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function contrast(a, b) {
  const L1 = relLum(a);
  const L2 = relLum(b);
  const hi = Math.max(L1, L2);
  const lo = Math.min(L1, L2);
  return (hi + 0.05) / (lo + 0.05);
}

export const clamp = (v, min = 0, max = 100) => Math.max(min, Math.min(max, v));

/* ============================================================
   AUTO-DERIVATION — one primary pick -> full ramp
   deep:  darker for hover/dark surfaces
   light: lighter for gradients
   soft:  solid light tint (focus rings, badges, chips)
   tint:  solid faint tint (selected-card backgrounds)

   NOTE: soft/tint are SOLID colors (not rgba alpha) so they match
   the hand-authored preset tints in _theme.scss / the landing
   prototype and render predictably on any surface, not just white.
   They keep the primary hue and push lightness very high while
   holding a little saturation so the color stays visible.
   ============================================================ */
// `deep` text is routinely paired with a `soft` badge/chip background of the
// same hue (see .ce-hours, quick-facts labels). A flat "l - 13" offset reads
// fine for some hues but leaves others (e.g. warm's terracotta) under 4.5:1
// against their own `soft` — so `deep` is walked further down in lightness,
// hue/saturation held fixed, until it clears WCAG AA against `soft`.
const MIN_DEEP_ON_SOFT_CONTRAST = 4.6;
export function deriveRamp(hex) {
  const { h, s, l } = rgbToHsl(...Object.values(hexToRgb(hex)));
  const soft = hslToHex(h, clamp(s + 6), clamp(l * 0.15 + 78));
  const deepSat = clamp(s + 4);
  let deepLightness = clamp(l - 13);
  let deep = hslToHex(h, deepSat, deepLightness);
  while (contrast(deep, soft) < MIN_DEEP_ON_SOFT_CONTRAST && deepLightness > 0) {
    deepLightness = clamp(deepLightness - 2);
    deep = hslToHex(h, deepSat, deepLightness);
  }
  return {
    base: String(hex).toUpperCase(),
    deep,
    light: hslToHex(h, clamp(s - 6), clamp(l + 13)),
    soft,
    tint: hslToHex(h, clamp(s + 3), clamp(l * 0.1 + 88)),
  };
}

// pick the legible text color to sit on a filled color
export function textOn(hex) {
  return contrast(hex, '#FFFFFF') >= contrast(hex, '#2D2A22') ? '#FFFFFF' : '#2D2A22';
}


/* ============================================================
   STATE <-> URL QUERY PARAMS
   ============================================================ */

// Parse a query string (e.g. window.location.search) into a theme-state
// object containing only the keys that are actually present.
export function parseThemeParams(search) {
  const params = new URLSearchParams(search || '');
  const state = {};
  THEME_PARAM_KEYS.forEach((key) => {
    if (params.has(key)) {
      const value = params.get(key);
      if (value !== null && value !== '') state[key] = value;
    }
  });
  return state;
}

// Build a query string from a theme-state object (skips empty values).
// Returns e.g. "thema=warm&primary=%23C87359&...".
export function buildThemeQuery(state) {
  const params = new URLSearchParams();
  THEME_PARAM_KEYS.forEach((key) => {
    const value = state && state[key];
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, value);
    }
  });
  return params.toString();
}


/* ============================================================
   APPLY — set the real CSS variables on a DOM element
   Variable names match resources/assets/sass/apps/event/shared/_theme.scss.
   Inline styles win over the body[data-theme] stylesheet rules.
   ============================================================ */
export function applyThemeToElement(el, state) {
  if (!el || !state) return;

  // 1. Preset key -> data-theme attribute (handled by _theme.scss).
  if (state.thema) {
    el.setAttribute('data-theme', state.thema);
  }

  const set = (name, value) => el.style.setProperty(name, value);

  // 2. Primary color -> brand ramp.
  if (state.primary) {
    const p = deriveRamp(state.primary);
    set('--brand', p.base);
    set('--brand-deep', p.deep);
    set('--brand-light', p.light);
    set('--brand-soft', p.soft);
    set('--btn-text', textOn(state.primary));
  }

  // 3. Secondary color -> accent ramp.
  if (state.secondary) {
    const s = deriveRamp(state.secondary);
    set('--accent-2', s.base);
    set('--accent-2-deep', s.deep);
    set('--accent-2-soft', s.soft);
  }

  // 4. Surfaces.
  if (state.bg) set('--bg', state.bg);
  if (state.surface) set('--surface', state.surface);

  // 5. Typography.
  if (state.headingFont) set('--serif', state.headingFont);
  if (state.bodyFont) set('--sans', state.bodyFont);
}

// True when the state carries at least one customizable color/font token.
export function hasColorTokens(state) {
  return COLOR_KEYS.concat(['headingFont', 'bodyFont']).some((k) => state && state[k]);
}
